import axios from 'axios';
import api from './api';
import type { InventoryBalance, InventoryItem, PaginatedResponse, SizeUnit } from '../types/venue';

const base = (venueId: string) => `/admin/venue/${venueId}/inventory/item`;

// ── Error extraction ──────────────────────────────────────────────────────────

const INVENTORY_ERROR_MESSAGES: Record<string, string> = {
  INVENTORY_ITEM_DUPLICATE: 'An item with this name already exists at this venue.',
  INVENTORY_ITEM_INACTIVE: 'This inventory item is inactive.',
  INVENTORY_ITEM_NOT_FOUND: 'Inventory item not found.',
  WINE_NOT_FOUND: 'Wine not found.',
  WINE_INACTIVE: 'This wine is inactive and cannot be tracked in inventory.',
  INVALID_REORDER_POINT: 'Reorder point must be less than or equal to par level.',
};

export function extractInventoryError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as Record<string, unknown> | undefined;
    const errorObj = data?.['error'] as Record<string, unknown> | undefined;
    const code = errorObj?.['code'];
    if (typeof code === 'string' && INVENTORY_ERROR_MESSAGES[code]) {
      return INVENTORY_ERROR_MESSAGES[code];
    }
    const message = errorObj?.['message'] ?? data?.['message'];
    if (typeof message === 'string' && message) return message;
    if (err.response?.status === 409) return INVENTORY_ERROR_MESSAGES['INVENTORY_ITEM_DUPLICATE'];
    if (err.response?.status === 403) return 'You do not have permission to perform this action.';
    if (err.response?.status === 404) return 'Inventory item not found.';
  }
  if (err instanceof Error && err.message) return err.message;
  return 'An unexpected error occurred. Please try again.';
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface WineListLink {
  _id: string;
  wineListId: string;
  wineListName: string;
  wineListStatus: string;
  visibility: 'LIVE' | 'HIDDEN';
  listPrice?: number;
  currency?: string;
  active: boolean;
}

/**
 * Derived stock status. Computed from balance.quantity + item.reorderPoint.
 * OUT  = quantity <= 0
 * LOW  = quantity > 0 AND reorderPoint set AND quantity <= reorderPoint
 * OK   = reorderPoint set AND quantity > reorderPoint
 * null = reorderPoint not set — no alert configured for this item
 */
export type StockStatus = 'OUT' | 'LOW' | 'OK';

/**
 * Balance shape returned by the item list endpoint.
 * The list endpoint returns a venue-wide aggregate (totalQuantity), not a raw balance document.
 * lastCost reflects the most recently updated location that has a cost recorded.
 */
export interface InventoryItemListBalance {
  totalQuantity: number;
  lastCost?: number | null;
}

/** Item as returned by GET /item (balance joined by backend). */
export type InventoryItemWithBalance = InventoryItem & {
  balance: InventoryItemListBalance | null;
  /** Derived by backend once Phase 10 backend is deployed. Optional for backward compat. */
  stockStatus?: StockStatus | null;
};

export type CreateItemPayload =
  | {
      type: 'GENERIC';
      displayName: string;
      sizeValue?: number;
      sizeUnit?: SizeUnit;
      parLevel?: number;
      reorderPoint?: number;
      binLocation?: string;
      /** Phase A: enable lot/batch tracking for this item. Default false. */
      lotTracked?: boolean;
    }
  | {
      type: 'WINE';
      wineId: string;
      sizeValue?: number;
      sizeUnit?: SizeUnit;
      parLevel?: number;
      reorderPoint?: number;
      binLocation?: string;
      /** Phase A: enable lot/batch tracking for this item. Default false. */
      lotTracked?: boolean;
    };

export type UpdateItemPayload = {
  parLevel?: number;
  reorderPoint?: number;
  active?: boolean;
  binLocation?: string | null;
  /** Phase A: enable or disable lot/batch tracking. */
  lotTracked?: boolean;
};

// ── Service ───────────────────────────────────────────────────────────────────

const inventoryItemService = {
  /**
   * Get all active inventory items for a venue.
   * Balance is joined per item by the backend — access via item.balance.
   * status param filters by derived stock status (LOW/OUT/OK) — requires Phase 10 backend.
   */
  getItems: async (
    venueId: string,
    page = 1,
    pageSize = 50,
    sort = 'displayName',
    order: 'asc' | 'desc' = 'asc',
    searchQuery?: string,
    locationId?: string,
    status?: StockStatus,
    activeFilter?: 'active' | 'inactive' | 'all',
  ): Promise<PaginatedResponse<InventoryItemWithBalance>> => {
    const params: Record<string, unknown> = { page, pageSize, sort, order };
    if (searchQuery?.trim()) params.q = searchQuery.trim();
    if (locationId) params.locationId = locationId;
    if (status) params.status = status;
    if (activeFilter && activeFilter !== 'active') params.active = activeFilter;
    const response = await api.get(base(venueId), { params });
    return response.data;
  },

  /** Get a single inventory item (without joined balance). */
  getItem: async (venueId: string, itemId: string): Promise<InventoryItem> => {
    const response = await api.get(`${base(venueId)}/${itemId}`);
    return response.data;
  },

  /** Create a new inventory item. For GENERIC: displayName sent directly. For WINE: wineId sent, displayName generated server-side. */
  createItem: async (venueId: string, payload: CreateItemPayload): Promise<InventoryItem> => {
    const response = await api.post(base(venueId), payload);
    return response.data;
  },

  /** Update mutable config fields only (parLevel, reorderPoint, active). */
  updateItem: async (
    venueId: string,
    itemId: string,
    payload: UpdateItemPayload,
  ): Promise<InventoryItem> => {
    const response = await api.put(`${base(venueId)}/${itemId}`, payload);
    return response.data;
  },

  /**
   * Soft-delete an inventory item (sets active = false).
   * Operational history is preserved. The item disappears from default list views
   * and remains accessible via the inactive/all filter.
   */
  deleteItem: async (venueId: string, itemId: string): Promise<void> => {
    await api.delete(`${base(venueId)}/${itemId}`);
  },

  /** Get all wine list items linked to this inventory item across all wine lists. */
  getWineListLinks: async (venueId: string, itemId: string): Promise<WineListLink[]> => {
    const response = await api.get(`${base(venueId)}/${itemId}/wine-list-links`);
    return response.data;
  },
};

export default inventoryItemService;
